// Pure helpers for the optimistic update inside `useUpsertInventoryValueList`.
//
// Why this exists separately: the optimistic update for CREATEs used to blindly
// push a fresh temp-id row on every call. Two CREATEs for the same cell (e.g.
// user types → clears → types again before the server has reconciled the first
// temp-id) left multiple cache entries for one logical cell. The backend
// deduplicates via `upsertByCellKey` and the WebSocket only maps the *first*
// temp-id to the real id (subsequent temps stay stranded by the
// `existingIdSet.has(realId)` guard in inventory-websocket.service.ts), so the
// duplicates persist client-side. Consumers that look up by cell coordinates
// then pick a stale entry — manifested as "old value reappears" / "updates
// frozen on touched cells" in the GPC energy tab.
//
// Fix: identify the cell up-front and merge a CREATE into any existing pending
// entry (temp OR real) for the same cell, instead of pushing a new row.

import type { UpsertInventoryValueInput } from "@/graphql/generated/graphql";

// The dimensional fields that together identify one logical cell in the
// inventory grid. Mirrors the backend's `InventoryValueCellKey`
// (inventory-value.models.ts) plus `type` / `streetType` / `scope`.
// inventoryId is intentionally excluded — callers already scope the list to
// a single inventory before invoking these helpers.
export type CellCoords = {
  readonly energySourceId?: string | null;
  readonly sectorId?: string | null;
  readonly subsectorId?: string | null;
  readonly subsubsectorId?: string | null;
  readonly sectorSliceId?: string | null;
  readonly type?: string | null;
  readonly streetType?: string | null;
  readonly scope?: number | null;
  // Per-gas discriminator for GPC waste Factor rows; null elsewhere. Part of the
  // cell identity so a cell's three per-gas factors don't merge into one entry.
  readonly gas?: string | null;
};

// The minimum shape a list entry must have for these helpers to work with it.
// Both `InventoryValue` from GraphQL and the loosely-typed cache entry shape
// used in inventory-websocket.service.ts satisfy this.
export type InventoryValueEntry = CellCoords & {
  readonly id: string;
  // createdAt/updatedAt may be either ISO strings (optimistic) or Date objects
  // (WS-hydrated). Consumers must tolerate both.
  readonly createdAt?: string | Date;
  readonly updatedAt?: string | Date;
};

const norm = <T extends string | number>(v: T | null | undefined): T | null => v ?? null;

// Two entries refer to the same cell when every dimensional field matches.
export const isSameCell = (a: CellCoords, b: CellCoords): boolean =>
  norm(a.energySourceId) === norm(b.energySourceId) &&
  norm(a.sectorId) === norm(b.sectorId) &&
  norm(a.subsectorId) === norm(b.subsectorId) &&
  norm(a.subsubsectorId) === norm(b.subsubsectorId) &&
  norm(a.sectorSliceId) === norm(b.sectorSliceId) &&
  norm(a.type) === norm(b.type) &&
  norm(a.streetType) === norm(b.streetType) &&
  norm(a.scope) === norm(b.scope) &&
  norm(a.gas) === norm(b.gas);

// A CREATE input has enough dimensional info to identify its target cell.
// (UPDATE inputs typically carry only `{id, inventoryId, amount}` — no cell
// dimensions — so cell-matching wouldn't be meaningful.)
const hasCellIdentity = (input: UpsertInventoryValueInput): boolean =>
  Boolean(input.sectorId) && Boolean(input.type);

const isTempId = (id: string): boolean => id.startsWith("temp-");

// Drop temp-id entries whose cell coords match any real-id entry in `realUpdates`.
// Used by the WebSocket reconciler to clean up orphan temps left behind when an
// optimistic CREATE pushed a temp-id but the backend's WS payload arrived without
// an `idMap` entry to swap it (e.g. the request didn't include `tempId`). Without
// this, the orphan temp persists in cache and `cellStore.find()` returns it
// instead of the canonical real entry.
export const reapOrphanTemps = <T extends InventoryValueEntry>(
  list: ReadonlyArray<T>,
  realUpdates: ReadonlyArray<InventoryValueEntry>,
): T[] => {
  const reals = realUpdates.filter((u) => !isTempId(u.id));
  if (reals.length === 0) return [...list];
  return list.filter((entry) => !isTempId(entry.id) || !reals.some((u) => isSameCell(entry, u)));
};

// Apply one upsert input to the working list, returning a new list. Entries
// are replaced (new objects), never mutated in place. The returned array is
// always a fresh reference so React Query subscribers see a change.
export const applyOptimisticUpsert = <T extends InventoryValueEntry>(
  list: ReadonlyArray<T>,
  input: UpsertInventoryValueInput,
  inventoryId: string,
  nowIso: string,
  tempIdFactory: () => string,
): T[] => {
  const next: T[] = [...list];

  // UPDATE path: caller provided an id. Match by id; spread input over the
  // existing entry. If the id is missing from the cache (stale id), fall
  // through to cell-matching so we don't push a phantom row — but only when
  // the input actually carries cell coords. A stale-id UPDATE without cell
  // coords (the typical {id, inventoryId, amount} shape) has nothing to
  // match against; pushing it would create a phantom row that's invisible
  // to cellStore lookups (no dimensions) and references a non-existent
  // backend id. Bail out instead.
  if (input.id) {
    const byId = next.findIndex((iv) => iv.id === input.id);
    if (byId >= 0) {
      next[byId] = mergeInto(next[byId], input, nowIso);
      return next;
    }
    if (!hasCellIdentity(input)) return next;
  }

  // CREATE path (or UPDATE with a stale id): try to find an existing entry
  // for the same cell. Prefer real-id entries over temp-id entries so that a
  // late CREATE doesn't accidentally overwrite the canonical row's id.
  if (hasCellIdentity(input)) {
    const realIndex = next.findIndex((iv) => !isTempId(iv.id) && isSameCell(iv, input));
    const matchIndex =
      realIndex >= 0 ? realIndex : next.findIndex((iv) => isTempId(iv.id) && isSameCell(iv, input));
    if (matchIndex >= 0) {
      // Preserve the existing entry's id — input.id may be a stale real id
      // (deleted server-side) and we mustn't overwrite the canonical id with it.
      next[matchIndex] = mergeInto(next[matchIndex], input, nowIso, { preserveId: true });
      return next;
    }
  }

  // Truly new cell: push a placeholder. Always mint a temp id here so that
  // `reapOrphanTemps` can clean up duplicates later — if the caller's stale
  // real id leaked into the pushed entry it would become an undeletable
  // phantom (the WS reconciler never reaps real-id entries).
  next.push(buildNewEntry<T>(input, inventoryId, nowIso, tempIdFactory));
  return next;
};

// ---- internals ----

const mergeInto = <T extends InventoryValueEntry>(
  existing: T,
  input: UpsertInventoryValueInput,
  nowIso: string,
  opts: { preserveId?: boolean } = {},
): T => {
  // The runtime shape after merging is a superset of T's known fields (existing
  // T merged with input's optional fields, then updatedAt overwritten and
  // optionally id preserved). We isolate the cast here so callers stay typed.
  const merged: InventoryValueEntry = {
    ...existing,
    ...input,
    updatedAt: nowIso,
    ...(opts.preserveId ? { id: existing.id } : {}),
  };
  return merged as T;
};

const buildNewEntry = <T extends InventoryValueEntry>(
  input: UpsertInventoryValueInput,
  inventoryId: string,
  nowIso: string,
  tempIdFactory: () => string,
): T => {
  // A brand-new optimistic entry can't satisfy T's required fields in general
  // (the cache type may require fields like `amount: number` that the input
  // makes optional). We accept this single cast at construction time — the
  // entry will be replaced wholesale by the WS payload on reconciliation.
  //
  // Spread `input` first, then write back narrowed `id` / `inventoryId` so the
  // generated `InputMaybe<string>` types in the input don't leak through.
  // The id MUST be a temp id: a stale real id from the caller would create an
  // undeletable phantom (reapOrphanTemps only sweeps temp-* entries).
  const id = input.id && isTempId(input.id) ? input.id : tempIdFactory();
  const entry: InventoryValueEntry & { inventoryId: string; createdAt: string; updatedAt: string } = {
    ...input,
    id,
    inventoryId,
    createdAt: nowIso,
    updatedAt: nowIso,
  };
  return entry as unknown as T;
};
